import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Divider, Collapse, Tag, Spin, Alert, Tooltip } from "antd";
import {
  faFileCircleCheck,
  faBuilding,
  faServer,
  faGears,
  faEthernet,
  faBuildingShield,
  faElevator,
  faUsers,
  faEnvelopeCircleCheck,
  faUpDown,
  faTable,
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
  faCodeBranch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { extractAdapterKeys } from "../../components/adapters/adapterMatrixInputs";
// COMPONENTS
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
// REDUX
import {
  usePatchOrganizationSettingsMutation,
  useLazyGetOrganizationAndChildsQuery,
} from "../../../redux/organizations/organizationAPI";
import { usePostUserMutation } from "../../../redux/users/userAPI";
import { useLazyGetAdaptersQuery } from "../../../redux/adapters/adapterAPI";
import { useLazyGetBanksByServerQuery } from "../../../redux/banks/bankAPI";
import { useLazyGetServerQuery } from "../../../redux/servers/serverAPI";
import { useDeleteByOrgMutation } from "../../../redux/wizardDrafts/wizardDraftsAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { useWizard } from "../WizardContext";
import { WizardOpGate } from "../WizardStepGate";
import { WizardStep } from "../utils/wizardInvalidation";
// STORE
import { RootState } from "../../../app/store";
// WIZARD COMPONENTS
import WizardExportButton from "../components/WizardExportButton";
import WizardImportDialog from "../components/WizardImportDialog";
// VALIDATION
import { isCdoValid, getCdoMissingMessage } from "./StepAdaptersCdoEditor";
import type { CdoStateBundle } from "./StepAdaptersCdoEditor";
import type { EditorModbusTemplate } from "./CdoModbusEditor";
import { extractCdoState } from "../../edgeManagement/adapters/extractCdoState";
import {
  extractMappedMessageTypes,
  extractModbusRegisterTypeMappedTypes,
  detectOverlaps,
} from "./BankOffsetMatrix";
import type { AdapterKeyInfo, MappedMessageType } from "./BankOffsetMatrix";
// OVERLAP DETECTION
import {
  detectMatrixOverlaps,
  intraLookup,
} from "../../components/adapters/offsetOverlapDetection";
import { isLn1FamilyProtocol } from "../../components/adapters/offsetMatrixHelpers";
// CONSTANTS
import {
  BLANK_ADAPTER_CDO_MCE,
  BLANK_ADAPTER_CDO_LN1,
  BLANK_ADAPTER_CDO_OPC,
  BLANK_ADAPTER_CDO_OPCUA,
  BLANK_ADAPTER_CDO_LOBBYVISION2,
  BLANK_ADAPTER_CDO_SEQUENT,
} from "../../../constants/adapter";
import {
  buildAdapterOffsetColumns,
  buildAdapterOffsetMap,
  computeAdapterMappedTypes,
  deserializeApiKeys,
  deserializeOffsetMapping,
  normalizeOffsetMap,
  toAdapterColumnDescriptors,
} from "../../components/adapters/AdapterEditor";
import { buildMatrixColumnSpecs } from "../../components/adapters/AdapterOffsetMatrix";
import { deriveUnitCount } from "../../edgeManagement/keys/utils";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { formatUuidT } from "../../../flatbuffers/display";
import fetchAdapterDirect from "../../../flatbuffers/api/fetchAdapterDirect";
import { fetchFlatBufferBinary } from "../../../utils/flatbufferApi";
import { getBinaryCdoProtocolByKey } from "../editors/BinaryCdoProtocolRegistry";

const { Panel } = Collapse;

// ************************************************ */
// TYPES ****************************************** */
interface AdapterValidation {
  id: string;
  name: string;
  protocolType: string;
  valid: boolean;
  message: string;
}

interface BankValidation {
  id: string;
  name: string;
  hasOverlaps: boolean;
  overlapCount: number;
}

/** Required wizard steps (critical for publish).
 *
 * Step numbers come from `WizardStep` — the same source of truth the
 * step-router (`SetupWizardPage`) and the completion-tracker
 * (`WizardContext.completedSteps`) use. Hard-coding them here once led to
 * `Channel Modifiers` and `Banks` swapping step numbers, which silently
 * gated publish on the wrong checkbox; resolve via the constant so a
 * future re-order doesn't desync this list. */
const REQUIRED_STEPS: { step: number; label: string }[] = [
  { step: WizardStep.Organization, label: "Organization" },
  { step: WizardStep.Server, label: "Server" },
  { step: WizardStep.Adapters, label: "Adapters" },
  { step: WizardStep.Channels, label: "Channels" },
  { step: WizardStep.Banks, label: "Banks" },
  { step: WizardStep.Lifts, label: "Lifts & Floors" },
];

/** Optional wizard steps (non-critical warnings).
 *
 * `Channel Modifiers` belongs here, not in REQUIRED — a perfectly valid
 * setup may not need any modifiers, and `markStepCompleted` for that step
 * only fires when the operator actually saves at least one modifier. If
 * we required it, every minimal deploy would have to add a dummy
 * modifier just to clear the gate. */
const OPTIONAL_STEPS: { step: number; label: string }[] = [
  { step: WizardStep.ChannelModifiers, label: "Channel Modifiers" },
  { step: WizardStep.Bacnet, label: "BACnet" },
  { step: WizardStep.Users, label: "Users & Roles" },
  { step: WizardStep.Notifications, label: "Notifications" },
  { step: WizardStep.Actions, label: "Actions" },
  { step: WizardStep.MonitorGrid, label: "Monitor Grid" },
];

/** Check whether an adapter's offset matrix has unresolved overlaps.
 *
 *  Runs the adapter editor's *own* derivation end-to-end rather than a
 *  parallel implementation. The previous version rebuilt columns from
 *  LN2_MESSAGE_CATEGORIES with a hardcoded size of 1, defaulted the car
 *  count to 2, and read offsets via `entry.key`/`entry.initial`/`entry.delta`
 *  — fields the API never emits (real entries carry message_type /
 *  message_value / unit_number / offset). Every column therefore collapsed to
 *  offset 0 and Review reported overlaps on matrices the editor rendered
 *  clean. Reusing the exported helpers is what keeps the two screens from
 *  drifting again; do not reintroduce a local re-implementation here. */
export function checkAdapterMatrixOverlaps(
  adapter: any,
  cdo: CdoStateBundle,
  proto: string,
): boolean {
  // Only relevant for protocols that use the offset matrix.
  if (!isLn1FamilyProtocol(proto) && proto !== "MODBUS") return false;

  const offsetParsed = adapter.offsetMappingJson
    ? parseFbJson(adapter.offsetMappingJson)
    : adapter.offset_mapping;

  const ln1KeyMappings = deserializeApiKeys(adapter.keys ?? []);
  const mappedTypes = computeAdapterMappedTypes(
    proto,
    ln1KeyMappings,
    cdo.ln1IntraContentFlags ?? {},
    offsetParsed,
  );

  const { ln1UnitOffsets, ln1IndividualOffsets } = deserializeOffsetMapping(
    offsetParsed,
    toAdapterColumnDescriptors(mappedTypes),
  );

  const columns = buildAdapterOffsetColumns(
    proto,
    mappedTypes,
    ln1UnitOffsets,
    ln1IndividualOffsets,
    undefined,
  );
  const values = buildAdapterOffsetMap(
    proto,
    mappedTypes,
    columns,
    ln1UnitOffsets,
    ln1IndividualOffsets,
    cdo.modbusRegisterOffsets,
  );
  const specs = buildMatrixColumnSpecs(
    columns ?? [],
    mappedTypes,
    ln1KeyMappings,
    adapter.floors ?? 0,
    undefined,
  );
  if (specs.length === 0) return false;

  // Same car count the editor uses: derived from the offset mapping, floored
  // at 2 (a single-car matrix has no per-car delta to collide).
  const numCars = Math.max(2, deriveUnitCount(offsetParsed));
  const intraFn = (mcN: number, unitIdx: number) =>
    intraLookup(cdo.ln1IntraContentFlags, mcN, unitIdx);

  return detectMatrixOverlaps(specs, values, numCars, intraFn).size > 0;
}

/** Parse a JSON string field from FlatBuffer *T objects (stored as *Json suffixes).
 *  Falls back to null on missing/unparseable input. */
function parseFbJson(s: string | Uint8Array | null | undefined): any {
  if (!s) return null;
  try {
    return JSON.parse(s as string);
  } catch {
    return null;
  }
}

/** Build a minimal CdoStateBundle from raw adapter API data. */
function buildCdoFromAdapter(adapterData: any): CdoStateBundle {
  // Support both FlatBuffer path (camelCase *Json fields) and legacy JSON path.
  const cdoInputParsed = adapterData.cdoInputJson
    ? parseFbJson(adapterData.cdoInputJson)
    : adapterData.cdoInput;
  const offsetMapParsed = adapterData.offsetMappingJson
    ? parseFbJson(adapterData.offsetMappingJson)
    : adapterData.offset_mapping;
  const intraFlagsParsed = adapterData.intraContentFlagsJson
    ? parseFbJson(adapterData.intraContentFlagsJson)
    : adapterData.intra_content_flags;

  // Build an AdapterInterface-compatible shape for extractCdoState.
  // cdoBinary carries the OPC UA / LobbyVision2 mapping config (fetched from
  // the separate /adapters/:id/cdo endpoint by the caller). Without it,
  // extractCdoState cannot decode those binary-CDO protocols and the Review
  // reports a false "missing mapping".
  const adapterForExtract: any = {
    protocolType: adapterData.protocolType ?? adapterData.protocol_type,
    cdoInput: cdoInputParsed,
    cdoBinary: adapterData.cdoBinary ?? null,
    bacnetConfig: adapterData.bacnetConfig ?? [],
    bacnetBankMappings: adapterData.bacnetBankMappings ?? [],
  };
  const extracted = extractCdoState(adapterForExtract);

  // cdo_template: prefer parsed cdoTemplateJson, then legacy cdo_template object.
  const tplRaw = adapterData.cdoTemplateJson
    ? parseFbJson(adapterData.cdoTemplateJson)
    : adapterData.cdo_template;
  const modbusFromTemplate: EditorModbusTemplate =
    tplRaw && (tplRaw.reads || tplRaw.writes)
      ? {
          ...tplRaw,
          pollingInterval: BigInt(tplRaw.pollingInterval ?? 1000),
          reads: tplRaw.reads ?? [],
          writes: tplRaw.writes ?? [],
        }
      : { reads: [], writes: [], slaveId: 1, pollingInterval: BigInt(1000) };

  const cdo: CdoStateBundle = {
    cdoMce: extracted.cdoMce || { ...BLANK_ADAPTER_CDO_MCE },
    cdoLn1: extracted.cdoLn1 || { ...BLANK_ADAPTER_CDO_LN1 },
    cdoModbus: modbusFromTemplate,
    cdoOpc: extracted.cdoOpc || { ...BLANK_ADAPTER_CDO_OPC },
    cdoOpcUa: extracted.cdoOpcUa || { ...BLANK_ADAPTER_CDO_OPCUA },
    cdoLobbyVision2: extracted.cdoLobbyVision2 || { ...BLANK_ADAPTER_CDO_LOBBYVISION2 },
    cdoSequent: extracted.cdoSequent || { ...BLANK_ADAPTER_CDO_SEQUENT },
    cdoBacnet: extracted.cdoBacnet || [],
    bacnetBankMappings: [],
    cdoJsonFallback: extracted.cdoJsonFallback || "{}",
    ln1KeyMappings: [],
    ln1UnitOffsets: {},
    ln1IntraContentFlags: {},
  };
  if (Array.isArray(adapterData.keys) && adapterData.keys.length > 0) {
    cdo.ln1KeyMappings = deserializeApiKeys(adapterData.keys);
  }
  if (offsetMapParsed && typeof offsetMapParsed === "object") {
    cdo.ln1UnitOffsets = normalizeOffsetMap<number[]>(offsetMapParsed);
  }
  if (intraFlagsParsed && typeof intraFlagsParsed === "object") {
    cdo.ln1IntraContentFlags = normalizeOffsetMap<boolean[]>(intraFlagsParsed);
  }
  return cdo;
}

/** Parse stored offset data into keyed maps. */
function keyedMapsToPositional(
  stored: unknown,
  types: { categoryKey: string }[],
): Record<string, number>[] {
  if (!Array.isArray(stored) || stored.length === 0) return [];
  if (typeof stored[0] === "object" && !Array.isArray(stored[0])) {
    return stored as Record<string, number>[];
  }
  // Legacy positional format — convert to keyed
  return (stored as number[][]).map((row) => {
    const keyed: Record<string, number> = {};
    types.forEach((mt, i) => {
      keyed[mt.categoryKey] = row[i] ?? 0;
    });
    return keyed;
  });
}

// ************************************************ */
// COMPONENT ************************************** */
export default function StepReview() {
  const { theme } = useSelector((state: RootState) => state.home);
  const { state: wizardState, markStepCompleted, isStepCompleted } = useWizard();
  const navigate = useNavigate();
  const [completing, setCompleting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(false);
  const [skipWarnings, setSkipWarnings] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [namesWarning, setNamesWarning] = useState<string | null>(null);
  const [triggerPatchSettings] = usePatchOrganizationSettingsMutation();
  const [triggerDeleteDraftsByOrg] = useDeleteByOrgMutation();
  const [triggerPostUser] = usePostUserMutation();
  const [triggerGetAdapters] = useLazyGetAdaptersQuery();
  const [triggerGetBanksByServer] = useLazyGetBanksByServerQuery();
  const [triggerGetOrg] = useLazyGetOrganizationAndChildsQuery();
  const [triggerGetServer] = useLazyGetServerQuery();

  // Validation results
  const [adapterResults, setAdapterResults] = useState<AdapterValidation[]>([]);
  const [bankResults, setBankResults] = useState<BankValidation[]>([]);

  // Display names
  const [orgName, setOrgName] = useState<string>("");
  const [serverName, setServerName] = useState<string>("");

  // ************************************************ */
  // STEP COMPLETION CHECK ************************* */
  const missingRequired = useMemo(
    () => REQUIRED_STEPS.filter((s) => !isStepCompleted(s.step)),
    [wizardState.completedSteps],
  );

  const missingOptional = useMemo(
    () => OPTIONAL_STEPS.filter((s) => !isStepCompleted(s.step)),
    [wizardState.completedSteps],
  );

  // ************************************************ */
  // VALIDATION ************************************ */
  const runValidation = useCallback(async () => {
    if (!wizardState.serverId) return;
    setValidating(true);

    try {
      const token = await GETJwtToken();

      // Fetch adapters and banks in parallel
      const [adapterResult, bankResult] = await Promise.all([
        triggerGetAdapters({
          page: 1,
          limit: 9999,
          // Adapters are org-global (no server_id column); fetch all.
          searchAndFilterString: "",
          token,
        }).unwrap(),
        triggerGetBanksByServer({
          idServer: wizardState.serverId,
          page: 1,
          limit: 9999,
          searchAndFilterString: "",
          token,
        }).unwrap(),
      ]);

      const adapters = adapterResult?.data || [];
      const banks = bankResult?.data || [];

      // Adapters are org-global, so the list endpoint returns every adapter in
      // the org — including many this deployment never uses. Only adapters
      // referenced by a bank in this server belong to the org being published,
      // so restrict validation to those. Unused adapters must not be checked or
      // block publishing.
      const referencedAdapterIds = new Set<string>(
        banks
          .map((bank: any) => formatUuidT(bank.adapterId ?? bank.adapter_id))
          .filter((id: string) => Boolean(id)),
      );
      const referencedAdapters = adapters.filter((ad: any) =>
        referencedAdapterIds.has(formatUuidT(ad.id)),
      );

      // The list endpoint skips CDO hydration for performance, so cdoInputJson is empty.
      // Fetch each referenced adapter individually in parallel to get fully-hydrated CDO data.
      const fullAdapters: any[] = await Promise.all(
        referencedAdapters.map(async (ad: any) => {
          const adapterId = formatUuidT(ad.id);
          try {
            const direct = await fetchAdapterDirect(adapterId, token);
            const resolved: any = { ...(direct || ad) };
            // Binary-CDO protocols (OPC UA, LobbyVision2) keep their mapping
            // config in cdo_binary, served from the separate /adapters/:id/cdo
            // endpoint — NOT the main /adapters/:id record. Fetch it here so
            // the CDO validation below can decode it; otherwise a reloaded
            // draft always fails validation and cannot be published.
            const protoStr =
              typeof resolved.protocolType === "number"
                ? (AdapterProtocol[resolved.protocolType as AdapterProtocol] ?? "")
                : (resolved.protocol_type ?? "");
            if (getBinaryCdoProtocolByKey(protoStr)) {
              const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
              resolved.cdoBinary = await fetchFlatBufferBinary(
                `${apiBase}/adapters/${adapterId}/cdo`,
                token,
              );
            }
            return resolved;
          } catch {
            return ad; // fallback to list data if individual fetch fails
          }
        }),
      );

      // Validate each adapter CDO using the fully-hydrated adapter data.
      // Prefer pre-validated results from WizardContext (stored by the adapter
      // editor on save) so the Review page displays the same results each page
      // produced — eliminating the drift that caused this bug. Fall back to
      // re-validation only when no pre-validated result exists.
      const adapterValidations: AdapterValidation[] = fullAdapters.map((ad: any) => {
        const adId = formatUuidT(ad.id);
        const preValidated = wizardState.adapterValidations[adId];

        if (preValidated) {
          // Use the adapter editor's own validation result — this is the
          // single source of truth and ensures Review never drifts.
          return {
            id: adId,
            name: ad.name || adId,
            protocolType:
              (ad.protocolType != null
                ? AdapterProtocol[ad.protocolType as AdapterProtocol]
                : (ad.protocol_type ?? "")
              )?.toUpperCase() ?? "UNKNOWN",
            valid: preValidated.valid,
            message: preValidated.message,
          };
        }

        // Fallback: re-validate when no pre-validated result exists
        // (e.g., adapters created before this feature, or org restored from backup).
        const cdo = buildCdoFromAdapter(ad);
        const protoStr =
          typeof ad.protocolType === "number"
            ? AdapterProtocol[ad.protocolType as AdapterProtocol]
            : (ad.protocol_type ?? "");
        const proto = (protoStr || "").toUpperCase();
        let valid = isCdoValid(proto, cdo);
        let message = valid ? "Valid" : getCdoMissingMessage(proto, cdo);

        if (valid) {
          const overlapResult = checkAdapterMatrixOverlaps(ad, cdo, proto);
          if (overlapResult) {
            valid = false;
            message = "Adapter saved, but offset matrix has overlapping registers";
          }
        }

        return {
          id: adId,
          name: ad.name || adId,
          protocolType: proto,
          valid,
          message,
        };
      });
      setAdapterResults(adapterValidations);

      // Validate each bank for offset overlaps.
      // Pre-resolve per-adapter values once. Banks sharing the same adapter
      // reuse these without re-parsing the adapter's CDO on every iteration.
      interface AdapterOverlapCtx {
        adapter: any;
        adIntra: boolean;
        types: MappedMessageType[];
        adapterKeyInfos: AdapterKeyInfo[];
        categoryToRegType: Record<string, string>;
      }
      const adapterCtxById = new Map<string, AdapterOverlapCtx>();
      fullAdapters.forEach((ad: any) => {
        const adIntra = Boolean(ad.intraContentFlagsJson ?? ad.intra_content_flags);
        const adCdoInput = ad.cdoInputJson ? parseFbJson(ad.cdoInputJson) : ad.cdo_input;
        const adCdoTemplate = ad.cdoTemplateJson
          ? parseFbJson(ad.cdoTemplateJson)
          : ad.cdo_template;
        const cdoForExtract = adCdoTemplate || adCdoInput;
        adapterCtxById.set(formatUuidT(ad.id), {
          adapter: ad,
          adIntra,
          types: extractMappedMessageTypes(cdoForExtract, ad, adIntra),
          adapterKeyInfos: extractAdapterKeys(ad),
          categoryToRegType: extractModbusRegisterTypeMappedTypes(cdoForExtract).categoryToRegType,
        });
      });

      const bankValidations: BankValidation[] = banks.map((bank: any) => {
        // adapterId is UuidT on FlatBuffer path; fall back to snake_case on JSON path.
        const bankAdapterId = formatUuidT(bank.adapterId ?? bank.adapter_id);
        const ctx = adapterCtxById.get(bankAdapterId);
        // offsetMapping is camelCase on FlatBuffer path; parse from JSON string field.
        const bankOffsetMap = bank.offsetMapping ?? parseFbJson(bank.offsetMappingJson);
        if (!ctx || !bankOffsetMap || ctx.types.length === 0) {
          return {
            id: formatUuidT(bank.id),
            name: bank.name || formatUuidT(bank.id),
            hasOverlaps: false,
            overlapCount: 0,
          };
        }

        const offsets = keyedMapsToPositional(bankOffsetMap, ctx.types);
        // floorCount is camelCase on FlatBuffer path.
        const floorCount = bank.floorCount ?? bank.floor_count ?? 2;
        const overlaps = detectOverlaps(
          ctx.types,
          offsets,
          floorCount,
          ctx.adIntra,
          ctx.adapterKeyInfos,
          new Set<string>(),
          ctx.categoryToRegType,
        );

        return {
          id: formatUuidT(bank.id),
          name: bank.name || formatUuidT(bank.id),
          hasOverlaps: overlaps.size > 0,
          overlapCount: overlaps.size,
        };
      });
      setBankResults(bankValidations);
    } catch {
      setValidationError("Validation could not be completed — check your network connection");
    }

    setValidating(false);
    setValidated(true);
  }, [wizardState.serverId, triggerGetAdapters, triggerGetBanksByServer]);

  // Run validation on mount and when server changes
  useEffect(() => {
    runValidation();
  }, [runValidation]);

  // Fetch org and server names for display
  useEffect(() => {
    async function fetchNames() {
      const token = await GETJwtToken();
      let namesFailed = false;
      if (wizardState.organizationId) {
        try {
          const orgResult = await triggerGetOrg({
            orgId: wizardState.organizationId,
            token,
          }).unwrap();
          const org = orgResult?.data || orgResult;
          setOrgName(org?.name || "");
        } catch {
          namesFailed = true;
        }
      }
      if (wizardState.serverId) {
        try {
          const serverResult = await triggerGetServer({
            idServer: wizardState.serverId,
            token,
          }).unwrap();
          const server = serverResult?.data || serverResult;
          setServerName(server?.name || "");
        } catch {
          namesFailed = true;
        }
      }
      if (namesFailed) {
        setNamesWarning("Could not load entity names");
      }
    }
    fetchNames();
  }, [wizardState.organizationId, wizardState.serverId]);

  // ************************************************ */
  // COMPUTED VALIDATION STATE ********************* */
  const adapterFailures = adapterResults.filter((a) => !a.valid);
  const bankFailures = bankResults.filter((b) => b.hasOverlaps);
  const hasCriticalFailures =
    missingRequired.length > 0 || adapterFailures.length > 0 || bankFailures.length > 0;
  const hasWarnings = missingOptional.length > 0;
  const canPublish = !hasCriticalFailures && (!hasWarnings || skipWarnings);

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const getTotalLifts = () => {
    const liftsByBank = wizardState.liftIdsByBank;
    let total = 0;
    Object.keys(liftsByBank).forEach((bankId) => {
      total += liftsByBank[bankId].length;
    });
    return total;
  };

  const renderCountTag = (count: number, labelSingular: string, labelPlural: string) => {
    if (count === 0) {
      return <Tag color="default">Skipped</Tag>;
    }
    const tagLabel = count === 1 ? `1 ${labelSingular}` : `${count} ${labelPlural}`;
    return <Tag color="green">{tagLabel}</Tag>;
  };

  const renderIdTag = (id: string | null, label: string) => {
    if (!id) {
      return <Tag color="default">Skipped</Tag>;
    }
    return <Tag color="green">{label}</Tag>;
  };

  const handleComplete = async () => {
    if (!canPublish) return;
    setCompleting(true);
    const token = await GETJwtToken();

    // Create all pending users now that the org is being published
    if (wizardState.pendingUsers.length > 0 && wizardState.organizationId) {
      await Promise.allSettled(
        wizardState.pendingUsers.map((user) => {
          const body: Record<string, unknown> = {
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            org_id: wizardState.organizationId,
          };
          if (user.role_id) {
            body.role_id = user.role_id;
          }
          return triggerPostUser({ body, token }).unwrap();
        }),
      );
    }

    // Activate the organization (change status from DRAFT to ACTIVE)
    // Skip this when editing an already-ACTIVE organization
    if (wizardState.organizationId && wizardState.orgStatus !== "ACTIVE") {
      try {
        await triggerPatchSettings({
          id: wizardState.organizationId,
          body: { status: "ACTIVE" },
          token,
        }).unwrap();
      } catch {
        setStepError("Failed to activate organization — please try again");
        setCompleting(false);
        return;
      }
    }

    // Clean up all wizard drafts for this org (they served their purpose)
    if (wizardState.organizationId) {
      try {
        await triggerDeleteDraftsByOrg({ orgId: wizardState.organizationId, token }).unwrap();
      } catch {
        // Non-critical — drafts can be cleaned up later; do not block completion
      }
    }

    // The hardcoded `markStepCompleted(12)` here previously referred to the OLD
    // step indices and marked MonitorGrid complete instead of Review when
    // ChannelModifiers was inserted at index 5.
    markStepCompleted(WizardStep.Review);
    setCompleting(false);
    navigate("/organization-management/organizations");
  };

  // ************************************************ */
  // VALIDATION CHECKLIST RENDER ******************* */
  const renderValidationChecklist = () => {
    if (!validated && !validating) return null;

    return (
      <div style={{ marginBottom: 16 }}>
        <DrawerRowDivider icon={faFileCircleCheck} title="Pre-Publish Validation" />
        {validationError && (
          <Alert
            type="error"
            showIcon
            closable
            message={validationError}
            onClose={() => setValidationError(null)}
            style={{ marginBottom: 8 }}
          />
        )}
        <Spin spinning={validating} tip="Validating...">
          {/* REQUIRED STEPS */}
          <div style={{ marginBottom: 12 }}>
            <strong style={{ fontSize: 13 }}>Required Steps</strong>
            <div style={{ marginTop: 6 }}>
              {REQUIRED_STEPS.map((s) => {
                const completed = isStepCompleted(s.step);
                return (
                  <div
                    key={s.step}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 0",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={completed ? faCircleCheck : faCircleXmark}
                      style={{ color: completed ? "#52c41a" : "#ff4d4f" }}
                    />
                    <span style={{ color: completed ? undefined : "#ff4d4f" }}>{s.label}</span>
                    {!completed && (
                      <Tag color="red" style={{ fontSize: 10 }}>
                        Missing
                      </Tag>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ADAPTER VALIDATION */}
          {adapterResults.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong style={{ fontSize: 13 }}>Adapter CDO Validation</strong>
              <div style={{ marginTop: 6 }}>
                {adapterResults.map((ad) => (
                  <div
                    key={ad.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 0",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={ad.valid ? faCircleCheck : faCircleXmark}
                      style={{ color: ad.valid ? "#52c41a" : "#ff4d4f" }}
                    />
                    <span style={{ color: ad.valid ? undefined : "#ff4d4f" }}>{ad.name}</span>
                    <Tag color={ad.valid ? "green" : "red"} style={{ fontSize: 10 }}>
                      {ad.protocolType}
                    </Tag>
                    {!ad.valid && (
                      <span style={{ fontSize: 11, color: "#ff4d4f" }}>{ad.message}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* BANK OVERLAP VALIDATION */}
          {bankResults.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong style={{ fontSize: 13 }}>Bank Offset Overlap Check</strong>
              <div style={{ marginTop: 6 }}>
                {bankResults.map((bank) => (
                  <div
                    key={bank.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 0",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={bank.hasOverlaps ? faCircleXmark : faCircleCheck}
                      style={{
                        color: bank.hasOverlaps ? "#ff4d4f" : "#52c41a",
                      }}
                    />
                    <span
                      style={{
                        color: bank.hasOverlaps ? "#ff4d4f" : undefined,
                      }}
                    >
                      {bank.name}
                    </span>
                    {bank.hasOverlaps && (
                      <Tag color="red" style={{ fontSize: 10 }}>
                        {bank.overlapCount} overlap
                        {bank.overlapCount !== 1 ? "s" : ""}
                      </Tag>
                    )}
                    {!bank.hasOverlaps && (
                      <Tag color="green" style={{ fontSize: 10 }}>
                        No overlaps
                      </Tag>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* OPTIONAL STEPS (WARNINGS) */}
          {missingOptional.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong style={{ fontSize: 13 }}>Optional Steps</strong>
              <div style={{ marginTop: 6 }}>
                {OPTIONAL_STEPS.map((s) => {
                  const completed = isStepCompleted(s.step);
                  return (
                    <div
                      key={s.step}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "4px 0",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={completed ? faCircleCheck : faTriangleExclamation}
                        style={{
                          color: completed ? "#52c41a" : "#faad14",
                        }}
                      />
                      <span
                        style={{
                          color: completed ? undefined : "#faad14",
                        }}
                      >
                        {s.label}
                      </span>
                      {!completed && (
                        <Tag color="warning" style={{ fontSize: 10 }}>
                          Skipped
                        </Tag>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SUMMARY ALERTS */}
          {validated && hasCriticalFailures && (
            <Alert
              type="error"
              showIcon
              message="Cannot publish: critical validation failures"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {missingRequired.map((s) => (
                    <li key={s.step}>Required step &quot;{s.label}&quot; is not completed</li>
                  ))}
                  {adapterFailures.map((ad) => (
                    <li key={ad.id}>
                      Adapter &quot;{ad.name}&quot; ({ad.protocolType}): {ad.message}
                    </li>
                  ))}
                  {bankFailures.map((bank) => (
                    <li key={bank.id}>
                      Bank &quot;{bank.name}&quot; has {bank.overlapCount} register overlap
                      {bank.overlapCount !== 1 ? "s" : ""}
                    </li>
                  ))}
                </ul>
              }
              style={{ marginBottom: 8 }}
            />
          )}
          {validated &&
            !validationError &&
            !hasCriticalFailures &&
            hasWarnings &&
            !skipWarnings && (
              <Alert
                type="warning"
                showIcon
                message="Optional steps were skipped"
                description="Some optional configuration steps were not completed. You can continue anyway or go back to complete them."
                style={{ marginBottom: 8 }}
              />
            )}
          {validated && !validationError && !hasCriticalFailures && !hasWarnings && (
            <Alert
              type="success"
              showIcon
              message="All validations passed"
              description="This organization is ready to be published."
              style={{ marginBottom: 8 }}
            />
          )}
        </Spin>
      </div>
    );
  };

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={completing}>
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        <DrawerRowDivider icon={faFileCircleCheck} title="Review & Finish" />
        {stepError && (
          <Alert
            type="error"
            showIcon
            closable
            message={stepError}
            onClose={() => setStepError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        {namesWarning && (
          <Alert
            type="warning"
            showIcon
            closable
            message={namesWarning}
            onClose={() => setNamesWarning(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        <div className="container">
          <div className="row">
            <div className="col-12 mt-3">
              <span className="generalStyles__inputLabel" style={{ color: "#888" }}>
                Review the resources created during this wizard. You can go back to any step to make
                changes before completing setup.
              </span>
            </div>
          </div>

          {/* VALIDATION CHECKLIST */}
          <div className="row">
            <div className="col-12 mt-3">{renderValidationChecklist()}</div>
          </div>

          <div className="row">
            <div className="col-12 mt-3">
              <Collapse defaultActiveKey={["organization", "server"]}>
                {/* ORGANIZATION */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faBuilding} style={{ marginRight: 8 }} />
                      Organization
                    </span>
                  }
                  key="organization"
                  extra={renderIdTag(wizardState.organizationId, "Created")}
                >
                  <p>
                    <strong>Organization:</strong>{" "}
                    {orgName || wizardState.organizationId || "Not created"}
                  </p>
                </Panel>

                {/* SERVER */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faServer} style={{ marginRight: 8 }} />
                      Server
                    </span>
                  }
                  key="server"
                  extra={renderIdTag(wizardState.serverId, "Created")}
                >
                  <p>
                    <strong>Server:</strong> {serverName || wizardState.serverId || "Not created"}
                  </p>
                </Panel>

                {/* ADAPTERS */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faGears} style={{ marginRight: 8 }} />
                      Adapters
                    </span>
                  }
                  key="adapters"
                  extra={renderCountTag(wizardState.adapterIds.length, "adapter", "adapters")}
                >
                  {wizardState.adapterIds.length === 0 && <p>No adapters created.</p>}
                  {wizardState.adapterIds.map((id) => {
                    const adapterName = adapterResults.find((a) => a.id === id)?.name || id;
                    return (
                      <p key={id}>
                        <Tag>{adapterName}</Tag>
                      </p>
                    );
                  })}
                </Panel>

                {/* CHANNELS */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faEthernet} style={{ marginRight: 8 }} />
                      Channels
                    </span>
                  }
                  key="channels"
                  extra={renderCountTag(wizardState.channelIds.length, "channel", "channels")}
                >
                  <p>
                    <strong>Channels:</strong>{" "}
                    {wizardState.channelIds.length === 0
                      ? "None created"
                      : `${wizardState.channelIds.length} created`}
                  </p>
                </Panel>

                {/* BANKS */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faBuildingShield} style={{ marginRight: 8 }} />
                      Banks
                    </span>
                  }
                  key="banks"
                  extra={renderCountTag(wizardState.bankIds.length, "bank", "banks")}
                >
                  <p>
                    <strong>Banks:</strong>{" "}
                    {wizardState.bankIds.length === 0
                      ? "None created"
                      : `${wizardState.bankIds.length} created`}
                  </p>
                </Panel>

                {/* CHANNEL MODIFIERS — optional; reflects whether any
                   modifier exists (`hasChannelModifiers`, set by the resume
                   hydrator and by the step on every load). NOT the step's
                   completion flag: the step completes itself on entry, so
                   that flag only says it was visited. The export/import paths
                   for these rows live in the backend's stage-5 LNX
                   bundle (`channel_modifiers` table), which now includes
                   the schema-v3 `value_transform_mode`,
                   `value_range_start`, `value_range_end`, and
                   `value_transform_offset` columns. */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faCodeBranch} style={{ marginRight: 8 }} />
                      Channel Modifiers
                    </span>
                  }
                  key="channelModifiers"
                  extra={
                    wizardState.hasChannelModifiers ? (
                      <Tag color="green">Configured</Tag>
                    ) : (
                      <Tag>Optional — none added</Tag>
                    )
                  }
                >
                  <p>
                    <strong>Channel Modifiers:</strong>{" "}
                    {wizardState.hasChannelModifiers
                      ? "At least one modifier is configured for this server. Source/target leaf messages, modifier type, value-transform mode (passthrough / specific / transformative), and payload are captured in the stage-5 LNX export. BEFORE modifiers may also carry an ordered expected-message sequence that gates the source send and is captured in the stage-5 LNX export."
                      : "No channel modifiers configured. This step is optional — skip it for a basic setup that doesn't need cross-channel message translation."}
                  </p>
                </Panel>

                {/* LIFTS */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faElevator} style={{ marginRight: 8 }} />
                      Lifts
                    </span>
                  }
                  key="lifts"
                  extra={renderCountTag(getTotalLifts(), "lift", "lifts")}
                >
                  <p>
                    <strong>Total Lifts:</strong>{" "}
                    {getTotalLifts() === 0
                      ? "None created"
                      : `${getTotalLifts()} across ${
                          Object.keys(wizardState.liftIdsByBank).length
                        } banks`}
                  </p>
                </Panel>

                {/* USERS & ROLES */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faUsers} style={{ marginRight: 8 }} />
                      Users & Roles
                    </span>
                  }
                  key="usersRoles"
                  extra={
                    <span>
                      {renderCountTag(wizardState.roleIds.length, "role", "roles")}{" "}
                      {renderCountTag(wizardState.pendingUsers.length, "user", "users")}
                    </span>
                  }
                >
                  <p>
                    <strong>Roles:</strong>{" "}
                    {wizardState.roleIds.length === 0
                      ? "None created"
                      : `${wizardState.roleIds.length} created`}
                  </p>
                  <p>
                    <strong>Users:</strong>{" "}
                    {wizardState.pendingUsers.length === 0
                      ? "None queued"
                      : `${wizardState.pendingUsers.length} queued for creation`}
                  </p>
                </Panel>

                {/* NOTIFICATIONS */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faEnvelopeCircleCheck} style={{ marginRight: 8 }} />
                      Notifications
                    </span>
                  }
                  key="notifications"
                  extra={renderCountTag(
                    wizardState.notificationIds.length,
                    "notification",
                    "notifications",
                  )}
                >
                  <p>
                    <strong>Notifications:</strong>{" "}
                    {wizardState.notificationIds.length === 0
                      ? "None created"
                      : `${wizardState.notificationIds.length} created`}
                  </p>
                </Panel>

                {/* ACTIONS */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faUpDown} style={{ marginRight: 8 }} />
                      Actions
                    </span>
                  }
                  key="actions"
                  extra={renderCountTag(wizardState.actionCatalogIds.length, "action", "actions")}
                >
                  <p>
                    <strong>Action Catalogs:</strong>{" "}
                    {wizardState.actionCatalogIds.length === 0
                      ? "None created"
                      : `${wizardState.actionCatalogIds.length} created`}
                  </p>
                </Panel>

                {/* MONITOR GRID */}
                <Panel
                  header={
                    <span>
                      <FontAwesomeIcon icon={faTable} style={{ marginRight: 8 }} />
                      Monitor Grid
                    </span>
                  }
                  key="monitorGrid"
                  extra={renderIdTag(wizardState.monitorGridId, "Created")}
                >
                  <p>
                    <strong>Monitor Grid ID:</strong> {wizardState.monitorGridId || "Skipped"}
                  </p>
                </Panel>
              </Collapse>
            </div>
          </div>
        </div>

        {/* COMPLETE BUTTON */}
        <Divider />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", gap: 8 }}>
            {/* Backend stage 12 = Review/Final. Previously hardcoded as
                11 (pre-ChannelModifiers numbering), which downloaded the
                MonitorGrid scope instead of the full configuration. */}
            {/* 13 is the StageReview sentinel, the only value ResolveStagesToExport
                expands to every stage. 12 is Timed Actions, whose dependency walk
                omits Lifts & Floors (stage 6) — exporting it produced an org with
                zero lifts and zero lift_floors, which cannot stand a bank up. */}
            <WizardExportButton stage={13} orgId={wizardState.organizationId} />
            {/* Review has no create op: its writes, import included, are "update". */}
            <WizardOpGate op="update">
              <Button onClick={() => setImportVisible(true)}>Import Configuration</Button>
            </WizardOpGate>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            {/* Re-validate button */}
            <Button onClick={runValidation} loading={validating}>
              Re-validate
            </Button>

            {/* Skip Warnings button — only shown when warnings exist but no critical failures */}
            {validated && !hasCriticalFailures && hasWarnings && !skipWarnings && (
              <Button onClick={() => setSkipWarnings(true)} type="default">
                Continue Anyway
              </Button>
            )}

            {/* Publish / Complete button */}
            <Tooltip
              title={(() => {
                if (hasCriticalFailures)
                  return "Fix all critical validation failures before publishing";
                if (!validated) return "Validation in progress...";
                return "";
              })()}
            >
              <span>
                <WizardOpGate op="update">
                  <Button
                    type="primary"
                    onClick={handleComplete}
                    disabled={!canPublish || !validated}
                    loading={completing}
                    size="large"
                  >
                    {wizardState.orgStatus === "ACTIVE" ? "Save Changes" : "Complete Setup"}
                  </Button>
                </WizardOpGate>
              </span>
            </Tooltip>
          </div>
        </div>

        {wizardState.organizationId && (
          <WizardImportDialog
            visible={importVisible}
            onClose={() => setImportVisible(false)}
            orgId={wizardState.organizationId}
          />
        )}
      </div>
    </Spin>
  );
}
